import { useEffect } from 'react';

/**
 * An admin's custom CSS (#367), set in Admin → Server settings. 'all' applies to every page (e.g. the default themes'
 * color tokens); the others apply on top of it to the landing page, the sign-in pages, the child's agent page and the
 * student portal. Each is a stylesheet the server serves, linked after the app's own styles so its rules win.
 */
export type CssArea = 'all' | 'landing' | 'login' | 'agent' | 'student';

const id = (area: CssArea) => `cg-custom-css-${area}`;
const href = (area: CssArea, bust = '') => `/api/custom-css/${area}.css${bust ? `?v=${bust}` : ''}`;

/** Link one area's stylesheet (once); returns a function that removes it again. */
function loadCustomCss(area: CssArea): () => void {
  let link = document.getElementById(id(area)) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = id(area);
    link.rel = 'stylesheet';
    link.href = href(area);
    document.head.appendChild(link);
  }
  const added = link;
  return () => added.remove();
}

/** Use an area's custom CSS while this page is showing. */
export function useCustomCss(area: CssArea) {
  useEffect(() => loadCustomCss(area), [area]);
}

/** Reload every linked custom stylesheet (after an admin saves new CSS), so changes show without a page reload. */
export function refreshCustomCss() {
  const bust = String(Date.now());
  for (const area of ['all', 'landing', 'login', 'agent', 'student'] as CssArea[]) {
    const link = document.getElementById(id(area)) as HTMLLinkElement | null;
    if (link) link.href = href(area, bust);
  }
}
